/**
 * The evidence "Ask Iris" sends from "Last changes" (CU-869fn0gcp, plan § 4.3-4.4), with the BI dashboards'
 * `askEvidence.js` and the Activity's as the models: the three cards (`kpi`), the map (`change`), a cell's detail
 * (`change`), the "Movement by ACRISS" card — bars and table — (`change`), an ACRISS's row (`change`) and its
 * evolution curve (`curve`, phase 2). Labels are the screen's — office name, ACRISS, dates, length, window —
 * never an id (criterion 28 of CU-869fkdhjv); ids go in `params.ids`. The SIM state travels as a label. Nothing is
 * recomputed: what the server returned, in the page's unit. Pure and alias-free (relative imports only).
 */
import { canvasState } from '../../../../@utils/askIris/canvasState.js'
import { inUnit } from './amounts.js'
import { fmtInstant, deltaSentence } from './windowLabel.js'
import { activityHref } from './viewState.js'
import { decisionReason, reasonText } from '../../Activity/@services/decisionReason.js'
import { derivationEvidence } from '../../Activity/@services/askEvidence.js'
import { matchDecision } from './decisionRow.js'

export { shouldClearAskContext } from '../../Activity/@services/askEvidence.js'

const VIEW = 'ds_repricing.lastChanges'
const SCREEN = 'Last changes'
// What the figures COUNT, never why the engine decided: Iris once read "simulated and not-sent rows move nothing" as
// the cause of an unmoved price (2026-10-30). The engine's reason travels in `reason`, worded as Activity paints it,
// and its calculation in `derivation`, Activity's rail (v1.4).
export const DEFINITION = 'Last changes: per cell (ACRISS × pick-up date), your shelf price at the start of the window vs the target shelf price the engine set with its last counted decision, and the mean of the competitors the deciding strategy compared against. Counting rule (`counted`): sent or capped, or simulated and the engine would have sent it; else the price stays; latest wins. Simulated decisions count like written ones, marked `simulated`; nothing was written to the PMS for them (filter: `scope.simulated`). Resolutions and this rule say what the figures count, not why the engine decided: its calculation for the last pass is `derivation` (Activity log\'s "How this was calculated": quote its lines verbatim, in order, in its `unit`) and its reason is `reason` (quote it as is); without either, see Activity log (`activity_href`). Simulation is never a reason. Change % of a rate = end / start − 1; cards, ACRISS rows and Average of the sample = the average of each rate\'s change over comparable cells (every rate weighs the same); Δ vs market = your change − market change, in points.'
const MAX_CELLS = 6
const MAX_PUSHES = 6
/**
 * This screen's evidence budget (`fitBudget`): the brain reads up to 8,000 characters since its 0.134.0, and a cell
 * now carries the engine's reason and the Activity keys per pass. 7,000 leaves ~1,000 for the brain re-encoding
 * non-ASCII (— ’ “ grow when serialised with ensure_ascii). A long history still gets cut: the oldest passes go first,
 * the digest says `truncated` and `pushes_total`, and `selection.reason` (the last pass) is never dropped.
 */
export const CELL_ASK_BUDGET = 7000
const MAX_ACRISS = 8

const isNum = v => typeof v === 'number' && Number.isFinite(v)
const r2 = v => (isNum(v) ? Math.round(v * 100) / 100 + 0 : null)
const pct1 = ratio => (isNum(ratio) ? Math.round(ratio * 1000) / 10 + 0 : null)
const pts1 = v => (isNum(v) ? Math.round(v * 10) / 10 + 0 : null)

function compact(obj) {
	const out = {}
	for (const [k, v] of Object.entries(obj || {})) {
		if (v == null || v === '') continue
		if (Array.isArray(v) && !v.length) continue
		if (!Array.isArray(v) && typeof v === 'object' && !Object.keys(v).length) continue
		out[k] = v
	}
	return out
}

const WINDOW_TEXT = { last_7d: 'last 7 days', today: 'today', last_pass: 'last pass' }

/** The window as a question label: `{ lastDays }` for the day windows, a string otherwise. */
export function windowLabel(window) {
	if (!window) return null
	if (window.kind === 'custom' && window.days) return { lastDays: window.days }
	if (window.kind === 'last_7d') return { lastDays: 7 }
	return WINDOW_TEXT[window.kind] || null
}

/** `{ params, freshness }` the whole screen shares: the sample and the window in words, the ids apart. */
export function scopeOf(data, view) {
	const s = data?.sample || {}
	const w = data?.window || {}
	const tz = w.tz || null
	return {
		params: compact({
			scope: compact({
				office: s.office_name || s.office || null,
				length_days: s.lor ?? null,
				pickups: s.pickup_from && s.pickup_to ? { from: s.pickup_from, to: s.pickup_to } : null,
				window: w.kind === 'last_pass' ? 'last pass' : w.kind === 'custom' ? `last ${w.days} days` : WINDOW_TEXT[w.kind] || null,
				window_from: w.from ? fmtInstant(w.from, tz) : null,
				window_to: w.to ? fmtInstant(w.to, tz) : null,
				time_zone: tz,
				price: view?.unit === 'day' ? 'daily' : 'total',
				currency: 'EUR',
				mode: data?.mode === 'dry_run' ? 'simulation' : 'live',
				simulated: data?.scope?.simulated || null,
				tier: data?.tier?.none ? 'no tier' : data?.tier ? `${data.tier.providers} providers` : null,
			}),
			ids: compact({ office_code: s.office || null, pass_id: w.pass?.execution_id || null }),
		}),
		freshness: compact({ data_source: 'live', passes_in_window: data?.passes ?? null, last_pass_at: data?.last_pass?.ts ? fmtInstant(data.last_pass.ts, tz) : null }),
	}
}

const state = ({ selection, digest, scope }) => canvasState({
	view: VIEW, screen: SCREEN, selection, digest: { definition: DEFINITION, ...digest }, params: scope?.params || {}, freshness: scope?.freshness || {},
})

const sharedLabels = (data, window) => [data?.sample?.office_name || data?.sample?.office || null, data?.sample?.lor != null ? { days: data.sample.lor } : null, windowLabel(window)]

const pair = (start, end, unit, lor) => compact({ before: r2(inUnit(start, unit, lor)), after: r2(inUnit(end, unit, lor)) })

/**
 * One of the three cards: `card` ∈ `prices` | `tier` | `moved`. Nothing to ask without a figure.
 */
export function kpiEvidence({ card, data, view }) {
	if (!data?.headline) return null
	const h = data.headline
	const lor = data.sample?.lor || 1
	const unit = view?.unit || 'total'
	const subject = card === 'prices' ? 'prices' : card === 'tier' ? 'tier' : 'rates moved'
	let selection, value
	if (card === 'prices') {
		if (h.own?.change == null) return null
		value = `${pct1(h.own.change)}%`
		selection = compact({ card: 'Your prices', change_pct: pct1(h.own.change), ...pair(h.own.start, h.own.end, unit, lor), comparable_cells: h.own.cells, simulated_cells: h.own.sim_cells || null,
			tier_change_pct: pct1(h.market?.change), delta_points: pts1(h.delta_pt), reading: deltaSentence(h.delta_pt, h.own.change, h.market?.change) })
	} else if (card === 'tier') {
		if (h.market?.change == null) return null
		value = `${pct1(h.market.change)}%`
		selection = compact({ card: 'Your tier', change_pct: pct1(h.market.change), ...pair(h.market.start, h.market.end, unit, lor), cells_with_market: h.market.cells,
			providers: data.tier?.providers ?? null, strategies: (data.tier?.strategies || []).map(s => s.name) })
	} else {
		const m = h.moved
		if (!m || !m.m) return null
		value = `${m.n} of ${m.m}`
		selection = compact({ card: 'Rates moved', moved: m.n, of: m.m, down: m.down, up: m.up, no_net_change: m.unchanged, without_a_decision: m.no_decision, not_comparable: m.incomparable,
			acriss: (data.sample?.acriss || []).length, pickup_dates: (data.sample?.dates || []).length })
	}
	return {
		kind: 'kpi',
		budget: CELL_ASK_BUDGET,
		subject,
		labels: [{ value }, ...sharedLabels(data, data.window), data.mode === 'dry_run' ? 'simulation' : null],
		state: state({ selection, digest: compact({ unit: unit === 'day' ? 'per day' : `total for ${lor} days`, moved: h.moved }), scope: scopeOf(data, view) }),
	}
}

const cellLine = (c, unit, lor) => compact({
	acriss: c.sipp, pickup_date: c.pickup_date, your_change_pct: pct1(c.own_change), market_change_pct: pct1(c.market_change), delta_points: pts1(c.delta_pt),
	...pair(c.own_start, c.own_end, unit, lor), passes: c.n_passes, status: c.status, simulated: c.sim || null,
})

/** The map: the cells that moved most against the market, and what has no decision. */
export function gridEvidence({ data, view }) {
	const cells = (data?.cells || []).filter(c => c.status !== 'no_decision')
	if (!cells.length) return null
	const lor = data.sample?.lor || 1
	const unit = view?.unit || 'total'
	const withDelta = cells.filter(c => isNum(c.delta_pt))
	const byDelta = [...withDelta].sort((a, b) => a.delta_pt - b.delta_pt)
	return {
		kind: 'change',
		budget: CELL_ASK_BUDGET,
		labels: ['heat map', ...sharedLabels(data, data.window), data.mode === 'dry_run' ? 'simulation' : null],
		state: state({
			selection: compact({ element: 'Market and engine, pick-up by pick-up', cells: cells.length, moved: data.headline?.moved, delta_points_of_sample: pts1(data.headline?.delta_pt) }),
			digest: compact({
				most_below_market: byDelta.slice(0, MAX_CELLS).map(c => cellLine(c, unit, lor)),
				most_above_market: byDelta.length > MAX_CELLS ? byDelta.slice(-3).reverse().map(c => cellLine(c, unit, lor)) : null,
				thin_cells: cells.filter(c => c.thin).length,
				unit: unit === 'day' ? 'per day' : `total for ${lor} days`,
			}),
			scope: scopeOf(data, view),
		}),
	}
}

const acrissLine = (r, unit, lor) => compact({
	acriss: r.sipp, your_change_pct: pct1(r.own?.change), market_change_pct: pct1(r.market?.change), delta_points: pts1(r.delta_pt),
	your_price: pair(r.own?.start, r.own?.end, unit, lor), cells: r.cells,
})

/**
 * The "Movement by ACRISS" table: every ACRISS's average of each rate's change vs the tier, ordered by Δ (the ones that moved most
 * against the tier first), and the "Average of the sample" = the headline. Nothing to ask without a row with a figure.
 */
export function tableEvidence({ data, view, metric = null }) {
	const rows = (data?.by_acriss || []).filter(r => r.own?.change != null)
	if (!rows.length) return null
	const lor = data.sample?.lor || 1
	const unit = view?.unit || 'total'
	const h = data.headline || {}
	const byDelta = [...rows].sort((a, b) => (isNum(a.delta_pt) ? a.delta_pt : Infinity) - (isNum(b.delta_pt) ? b.delta_pt : Infinity))
	return {
		kind: 'change',
		budget: CELL_ASK_BUDGET,
		labels: ['movement by ACRISS', ...sharedLabels(data, data.window), data.mode === 'dry_run' ? 'simulation' : null],
		state: state({
			selection: compact({
				element: 'Movement by ACRISS', acriss: rows.length, pickup_dates: (data.sample?.dates || []).length,
				// phase 2: the bars above the table (you vs tier per ACRISS), as the user is reading them
				chart: metric ? `bars of you vs your tier per ACRISS, in ${metric === 'eur' ? 'euros of change' : 'percent of change'}` : null,
				average_of_the_sample: compact({ your_change_pct: pct1(h.own?.change), market_change_pct: pct1(h.market?.change), delta_points: pts1(h.delta_pt),
					reading: deltaSentence(h.delta_pt, h.own?.change, h.market?.change) }),
			}),
			digest: compact({
				most_below_tier: byDelta.slice(0, MAX_ACRISS).map(r => acrissLine(r, unit, lor)),
				most_above_tier: byDelta.length > MAX_ACRISS ? byDelta.slice(-3).reverse().map(r => acrissLine(r, unit, lor)) : null,
				without_a_tier: (data.by_acriss || []).filter(r => r.own?.change != null && r.market?.change == null).map(r => r.sipp),
				unit: unit === 'day' ? 'per day' : `total for ${lor} days`,
			}),
			scope: scopeOf(data, view),
		}),
	}
}

const RESOLUTION_WORD = { sent: 'sent', capped: 'capped', already_set: 'already set', no_price_change: 'no price change', simulated: 'simulated', not_sent: 'not sent' }

/**
 * The Reason line Activity paints for a pass's decision (`decisionReason`, the detail's own builder), from the fields
 * `/pushes` carries of the chosen row: the engine's `reason` and its incomplete-market block. `null` when the engine
 * recorded none — never a sentence of ours.
 */
export function pushReason(p, lor) {
	if (!p) return null
	return decisionReason({
		event_type: p.event_type, reason: reasonText(p.engine_reason), payload: { reason: p.engine_reason || null },
		derivation: p.incomplete_market ? { incomplete_market: p.incomplete_market } : null,
	}, lor)
}

/** The machine keys of a pass's Activity row and the screen's "Open in Activity" link (no pass → the cell's). */
function activityKeys(p, cell, data) {
	const keys = { execution_id: p?.pass || null, office: data?.sample?.office || null, sipp: p?.sipp || cell?.sipp || null,
		pickup_date: p?.pickup_date || cell?.pickup_date || null, lor: data?.sample?.lor ?? null }
	return {
		activity_row: compact(keys),
		activity_href: activityHref({ execution_id: keys.execution_id, office: keys.office, sipp: keys.sipp, pickup_from: keys.pickup_date, lor: keys.lor }),
	}
}

const pushLine = (p, unit, lor, tz, cell, data) => compact({
	pass_at: fmtInstant(p.ts, tz), strategy: p.strategy_name, product: p.product_name,
	market: pair(p.market_before, p.market_after, unit, lor), your_price: pair(p.own_before, p.own_after, unit, lor),
	// phase 2 (R6): what the next pass of the cell read on the site, beside the target ("after")
	observed_next_pass: r2(inUnit(p.own_observed_after, unit, lor)),
	your_change_pct: pct1(p.own_change), market_change_pct: pct1(p.market_change), delta_points: pts1(p.delta_pt),
	resolution: RESOLUTION_WORD[p.resolution] || String(p.resolution || '').replace(/_/g, ' '), simulated: p.sim || null,
		counted: typeof p.counted === 'boolean' ? p.counted : null,
	reason: pushReason(p, lor), ...activityKeys(p, cell, data),
})

/**
 * The last pass's "How this was calculated" (CU-869fn0gcp v1.4): Activity's builder over the Activity row the dialog
 * read (`decisionRow.js`), only when that row is the pass's chosen one; else null. In `selection`, which `fitBudget`
 * never cuts: the lines go whole and in order.
 */
function lastDerivation(decision, last) {
	if (!decision || !last || matchDecision([decision], last) !== decision) return null
	const d = derivationEvidence(decision)
	return d.lines.length ? compact(d) : null
}

/**
 * A cell's detail: its two pairs and the passes that decided it, newest first, with their resolutions; the engine's
 * reason of the last pass in `selection.reason` and of each pass in its line, with the keys of its Activity row.
 * `decision`: the last pass's Activity row, when the dialog has read it → `selection.derivation`.
 */
export function cellEvidence({ cell, pushes = [], data, view, decision = null }) {
	if (!cell || cell.status === 'no_decision') return null
	const lor = data?.sample?.lor || 1
	const unit = view?.unit || 'total'
	const tz = data?.window?.tz || null
	const last = pushes[0] || null          // newest first: the cell's last pass
	return {
		kind: 'change',
		budget: CELL_ASK_BUDGET,
		labels: [cell.sipp, { date: cell.pickup_date }, ...sharedLabels(data, data?.window), cell.sim ? 'simulation' : null],
		state: state({
			selection: compact({
				acriss: cell.sipp, pickup_date: cell.pickup_date, your_price: pair(cell.own_start, cell.own_end, unit, lor), your_change_pct: pct1(cell.own_change),
				market: pair(cell.market_start, cell.market_end, unit, lor), market_change_pct: pct1(cell.market_change), delta_points: pts1(cell.delta_pt),
				reading: deltaSentence(cell.delta_pt, cell.own_change, cell.market_change), passes: cell.n_passes, competitors_min: cell.n_comp_min, status: cell.status,
				few_passes: cell.thin || null, simulated: cell.sim || null,
				reason: pushReason(last, lor), derivation: lastDerivation(decision, last), ...activityKeys(last, cell, data),
			}),
			digest: compact({ pushes: pushes.slice(0, MAX_PUSHES).map(p => pushLine(p, unit, lor, tz, cell, data)), pushes_total: pushes.length || null, unit: unit === 'day' ? 'per day' : `total for ${lor} days` }),
			scope: scopeOf(data, view),
		}),
	}
}

/** An ACRISS's row of "Movement by ACRISS": its average of each rate's change and its dates. */
export function acrissEvidence({ row, data, view }) {
	if (!row?.own || row.own.change == null) return null
	const lor = data?.sample?.lor || 1
	const unit = view?.unit || 'total'
	return {
		kind: 'change',
		budget: CELL_ASK_BUDGET,
		labels: [row.sipp, ...sharedLabels(data, data?.window)],
		state: state({
			selection: compact({
				acriss: row.sipp, your_price: pair(row.own.start, row.own.end, unit, lor), your_change_pct: pct1(row.own.change),
				market: pair(row.market?.start, row.market?.end, unit, lor), market_change_pct: pct1(row.market?.change), delta_points: pts1(row.delta_pt),
				reading: deltaSentence(row.delta_pt, row.own.change, row.market?.change), cells: row.cells,
				simulated_cells: row.own.sim_cells || null,
			}),
			digest: compact({ dates: (row.dates || []).slice(0, 8).map(d => compact({ pickup_date: d.pickup_date, your_change_pct: pct1(d.own_change), market_change_pct: pct1(d.market_change), delta_points: pts1(d.delta_pt), status: d.status, simulated: d.sim || null })), unit: unit === 'day' ? 'per day' : `total for ${lor} days` }),
			scope: scopeOf(data, view),
		}),
	}
}

const MAX_CURVE_PASSES = 12

/**
 * An ACRISS's evolution curve (phase 2, kind `curve`): the two lines pass by pass, as drawn — your average price and
 * your tier's over the dates each pass decided. The newest passes first when there are many; the pass time is the
 * label (the id goes in `params.ids`).
 */
export function curveEvidence({ sipp, curve, data, view }) {
	if (!sipp || !curve || curve.empty) return null
	const lor = data?.sample?.lor || 1
	const unit = view?.unit || 'total'
	const at = (s, d) => s?.points.find(p => p.d === d)?.value ?? null
	const [you, tier] = curve.main
	const points = curve.passes.map((p, d) => compact({ pass_at: curve.xLabels[d], pickup_dates_decided: p.dates, you: at(you, d), your_tier: at(tier, d), you_before: p.before }))
	const first = (s) => s?.points[0]?.value ?? null
	const last = (s) => s?.points[s.points.length - 1]?.value ?? null
	return {
		kind: 'curve',
		budget: CELL_ASK_BUDGET,
		labels: [sipp, ...sharedLabels(data, data?.window), data?.mode === 'dry_run' ? 'simulation' : null],
		state: state({
			selection: compact({
				element: `${sipp} · average price by pass`, acriss: sipp, passes: curve.passes.length,
				you: compact({ first: first(you), last: last(you) }), your_tier: compact({ first: first(tier), last: last(tier) }),
			}),
			digest: compact({
				points: points.length > MAX_CURVE_PASSES ? points.slice(-MAX_CURVE_PASSES) : points,
				points_total: points.length > MAX_CURVE_PASSES ? points.length : null,
				how: 'each point = the mean over the pick-up dates that pass decided for this ACRISS; you = your price after the pass (the target when the write landed), tier = the competitors the strategy compared with',
				unit: unit === 'day' ? 'per day' : `total for ${lor} days`,
			}),
			scope: scopeOf(data, view),
		}),
	}
}
